import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, doc, docData, addDoc, updateDoc, query, where, serverTimestamp, arrayUnion, arrayRemove, Timestamp, getDoc, writeBatch, orderBy, getDocs, runTransaction, deleteDoc, or, setDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable, HttpsCallableResult } from '@angular/fire/functions';
import { Observable, of, from, combineLatest, BehaviorSubject } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { WorkshopWork, WorkStatus, Product, WorkItem, StatusLogEntry, WorkCurrentStatus, BillingStatus, NotifiedStatus, Client, Bike, Offer, AppointmentStatus, Appointment, Employee, CommissionParams, VehicleInspection } from './models';
import { HttpClient } from '@angular/common/http';

interface WorkCounts {
  [status: string]: number;
}

@Injectable({ providedIn: 'root' })
export class FirestoreDataService {
  private readonly worksCollection = 'workshop_works';
  private readonly statusCollection = 'status';
  private readonly productsCollection = 'product';
  private readonly clientsCollection = 'bap_clients';
  private readonly inspectionsCollection = 'bap_inspections';
  private readonly bikesCollection = 'bap_bikes';
  private readonly countersCollection = 'counter';
  private readonly offersCollection = 'ofertas';
  private readonly appointmentsCollection = 'bap_appointments';

  private readonly EMAIL_API_URL = 'https://bap-mail-api.netlify.app/.netlify/functions';


  // --- SUJETOS DE ESTADO (STATE) ---
  private readonly openWorksSubject = new BehaviorSubject<WorkshopWork[]>([]);
  private readonly readyWorksSubject = new BehaviorSubject<WorkshopWork[]>([]);
  private readonly completedWorksSubject = new BehaviorSubject<WorkshopWork[]>([]);
  private readonly clientsSubject = new BehaviorSubject<Client[]>([]);
  private readonly productsSubject = new BehaviorSubject<Product[]>([]);
  
  // --- OBSERVABLES PÚBLICOS ---
  public openWorks$ = this.openWorksSubject.asObservable();
  public readyWorks$ = this.readyWorksSubject.asObservable();
  public completedWorks$ = this.completedWorksSubject.asObservable();
  public clients$ = this.clientsSubject.asObservable();
  public products$ = this.productsSubject.asObservable();

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private http: HttpClient
  ) {}

  // --- MÉTODOS DE CARGA (LOAD) ---
  loadOpenWorkshopWorks(): void {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("currentStatus", "in", ["Received", "In Progress", "Awaiting Parts"]), orderBy("entryDate", "asc"));
    from(getDocs(q)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorkshopWork))),
      catchError(err => { console.error("Error obteniendo trabajos abiertos:", err); return of([]); })
    ).subscribe(works => this.openWorksSubject.next(works));
  }

    getAllWorksForAnalytics(): Observable<WorkshopWork[]> {
    const worksRef = collection(this.firestore, this.worksCollection);
    // Usamos getDocs para obtener una "foto" instantánea para el cálculo
    return from(getDocs(worksRef)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorkshopWork))),
      catchError(err => { 
        console.error("Error obteniendo histórico de trabajos para analítica:", err); 
        return of([]); 
      })
    );
  }

  /**
   * Obtiene todos los estados para el módulo de analítica.
   * Necesario para calcular promedios de tiempo entre fases.
   */
  getAllStatusesForAnalytics(): Observable<WorkStatus[]> {
    const statusRef = collection(this.firestore, this.statusCollection);
    return from(getDocs(statusRef)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorkStatus))),
      catchError(err => { 
        console.error("Error obteniendo histórico de estados para analítica:", err); 
        return of([]); 
      })
    );
  }


  loadReadyWorks(): void {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("currentStatus", "in", ["Ready","Handed Off"]), orderBy("entryDate", "asc"));
    from(getDocs(q)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorkshopWork))),
      catchError(err => { console.error("Error obteniendo trabajos listos:", err); return of([]); })
    ).subscribe(works => this.readyWorksSubject.next(works));
  }

  loadCompletedWorks(): void {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef,  where("currentStatus", "in", ["Completed", "Cancelled"]), orderBy("entryDate", "desc"));
    from(getDocs(q)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorkshopWork))),
      catchError(err => { console.error("Error obteniendo trabajos completados:", err); return of([]); })
    ).subscribe(works => this.completedWorksSubject.next(works));
  }

   getMyWorks(email: string): Observable<WorkshopWork[]> {
    const ref = collection(this.firestore, this.worksCollection);
    const q = query(ref, where("clientEmail", "==", email), orderBy("entryDate", "desc"));
    return collectionData(q, { idField: 'id' }) as Observable<WorkshopWork[]>;
  }


  loadClients(): void {
    const cacheKey = 'clientsCache';
    const cachedData = sessionStorage.getItem(cacheKey);
    if (cachedData) { this.clientsSubject.next(JSON.parse(cachedData)); return; }
    const clientsRef = collection(this.firestore, this.clientsCollection);
    const q = query(clientsRef, orderBy("name", "asc"));
    from(getDocs(q)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as Client))),
      tap(clients => sessionStorage.setItem(cacheKey, JSON.stringify(clients))),
      catchError(err => { console.error("Error obteniendo clientes:", err); return of([]); })
    ).subscribe(clients => this.clientsSubject.next(clients));
  }

  loadProducts(): void {
    const cacheKey = 'productsCache';
    const cachedData = sessionStorage.getItem(cacheKey);
    if (cachedData) { this.productsSubject.next(JSON.parse(cachedData)); return; }
    const now = new Date();
    const offersRef = collection(this.firestore, this.offersCollection);
    const offersQuery = query(offersRef, where('DESDE', '<=', now), where('HASTA', '>=', now));
    const activeOffers$ = from(getDocs(offersQuery)).pipe(
      map(querySnapshot => {
        const offersMap = new Map<string, number>();
        querySnapshot.docs.forEach(doc => {
          const offer = doc.data() as Offer;
          if (offer.Referencia && typeof offer.OFERTA === 'number') {
            offersMap.set(offer.Referencia, offer.OFERTA);
          }
        });
        return offersMap;
      }),
      startWith(new Map<string, number>()),
      catchError(err => { console.error("Error obteniendo ofertas:", err); return of(new Map<string, number>()); })
    );
    const productsRef = collection(this.firestore, this.productsCollection);
    const products$ = from(getDocs(productsRef)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as any)))
    );
    combineLatest([products$, activeOffers$]).pipe(
      map(([productsFromDb, offersMap]) => {
        return productsFromDb.map(productDoc => {
          const originalPrice = typeof productDoc.PRECIO === 'number' ? productDoc.PRECIO : 0;
          let finalPrice = originalPrice;
          const offerDiscount = offersMap.get(productDoc.id);
          if (offerDiscount) { finalPrice = originalPrice - (originalPrice * offerDiscount); }
          return {
            id: productDoc.id,
            nombre: productDoc.NOMBRE || 'Nombre no disponible',
            precio: finalPrice,
            marca: productDoc.MARCA || 'Marca no disponible',
            modelo: productDoc.MODELO,
            tamano: productDoc.TAMANO,
            HIOPOS: productDoc.HIOPOS,
            Familia: productDoc.Familia,
            stock: productDoc.stock
          } as Product;
        });
      }),
      tap(products => sessionStorage.setItem(cacheKey, JSON.stringify(products))),
      catchError(err => { console.error(`Error crítico obteniendo productos con ofertas:`, err); return of([]); })
    ).subscribe(products => this.productsSubject.next(products));
  }

  // --- MÉTODOS RESTAURADOS PARA EL DASHBOARD ---
  getWorkshopWorkById(id: string): Observable<WorkshopWork | undefined> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${id}`);
    return docData(workDocRef, { idField: 'id' }) as Observable<WorkshopWork | undefined>;
  }

  getWorkStatus(workId: string): Observable<WorkStatus | null> {
    const statusQuery = query(collection(this.firestore, this.statusCollection), where('workId', '==', workId));
    return (collectionData(statusQuery, { idField: 'id' }) as Observable<WorkStatus[]>).pipe(
      map(statuses => {
        if (statuses.length > 0) {
          const statusDoc = statuses[0];
          if (statusDoc.history && statusDoc.history.length > 0) {
            statusDoc.history.sort((a, b) => {
                const tsA = a.timestamp?.toDate ? a.timestamp.toDate().getTime() : (a.timestamp instanceof Date ? a.timestamp.getTime() : 0);
                const tsB = b.timestamp?.toDate ? b.timestamp.toDate().getTime() : (b.timestamp instanceof Date ? b.timestamp.getTime() : 0);
                return tsB - tsA;
            });
          }
          return statusDoc;
        }
        return null;
      }),
      catchError(err => { console.error('Error obteniendo estado (FirestoreDataService):', err); return of(null); })
    );
  }

  async getNextWorkOrderNumber(): Promise<number> {
    const counterDocRef = doc(this.firestore, this.countersCollection, 'work_orders');
    try {
      const newNumber = await runTransaction(this.firestore, async (transaction) => {
        const counterDoc = await transaction.get(counterDocRef);
        if (!counterDoc.exists()) {
          transaction.set(counterDocRef, { currentNumber: 1 });
          return 1;
        }
        const currentNumber = Number(counterDoc.data()['currentNumber']) || 0;
        const newCurrentNumber = currentNumber + 1;
        transaction.update(counterDocRef, { currentNumber: newCurrentNumber });
        return newCurrentNumber;
      });
      return newNumber;
    } catch (error) {
      console.error("Error al obtener el siguiente número de trabajo:", error);
      throw new Error("No se pudo generar un número de caso único.");
    }
  }

  // --- MÉTODOS DE MODIFICACIÓN (CON RECARGA) ---
  async addWorkshopWork(work: Omit<WorkshopWork, 'id' | 'entryDate' | 'billingStatus' | 'currentStatus' | 'notifiedStatus' | 'totalPrice' | 'addedItems'>): Promise<string> {
    const worksRef = collection(this.firestore, this.worksCollection);
    const dataToSave: any = {
        ...work,
        clientItems: work.clientItems || [],
        entryDate: serverTimestamp(),
        totalPrice: 0,
        addedItems: [],
        billingStatus: 'pending',
        notifiedStatus: 'pending',
        currentStatus: 'Received'
    };
    const docRef = await addDoc(worksRef, dataToSave);
    await this.setWorkStatus(docRef.id, 'Received', 'Trabajo creado.');
    this.loadOpenWorkshopWorks(); // Recarga la lista de trabajos abiertos
    return docRef.id;
  }

  async setWorkStatus(workId: string, newStatusValue: WorkCurrentStatus, notes?: string): Promise<void> {
    const statusQuery = query(collection(this.firestore, this.statusCollection), where('workId', '==', workId));
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    try {
        const querySnapshot = await getDocs(statusQuery);
        const existingStatusDoc = querySnapshot.docs.length > 0 ? querySnapshot.docs[0] : null;
        const newLogEntry: StatusLogEntry = { status: newStatusValue, timestamp: new Date(), notes: notes || '' };
        const batch = writeBatch(this.firestore);
        batch.update(workDocRef, { currentStatus: newStatusValue });
        if (existingStatusDoc) {
            const statusDocRef = existingStatusDoc.ref;
            batch.update(statusDocRef, { currentStatus: newStatusValue, notes: notes || '', lastUpdate: serverTimestamp(), history: arrayUnion(newLogEntry) });
        } else {
            const statusRef = doc(collection(this.firestore, this.statusCollection));
            batch.set(statusRef, { workId: workId, currentStatus: newStatusValue, notes: notes || '', lastUpdate: serverTimestamp(), history: [newLogEntry] });
        }
        await batch.commit();
        // Recargamos todas las listas de trabajos
        this.loadOpenWorkshopWorks();
        this.loadReadyWorks();
        this.loadCompletedWorks();
    } catch (error) { console.error('Error en setWorkStatus (FirestoreDataService):', error); throw error; }
  }

  async reopenWork(workId: string): Promise<void> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    const statusDocQuery = query(collection(this.firestore, this.statusCollection), where('workId', '==', workId));
    try {
      const statusDocs = await getDocs(statusDocQuery);
      if (statusDocs.empty) { throw new Error("No se encontró el documento de estado para este trabajo."); }
      const statusDocRef = statusDocs.docs[0].ref;
      const newLogEntry: StatusLogEntry = { status: 'In Progress', timestamp: new Date(), notes: "Trabajo reabierto por el usuario." };
      const batch = writeBatch(this.firestore);
      batch.update(workDocRef, { currentStatus: 'In Progress' });
      batch.update(statusDocRef, {
        currentStatus: 'In Progress',
        notes: "Trabajo reabierto",
        lastUpdate: serverTimestamp(),
        history: arrayUnion(newLogEntry)
      });
      await batch.commit();
      this.loadOpenWorkshopWorks();
      this.loadReadyWorks();
      this.loadCompletedWorks();
    } catch (error) { console.error(`Error al reabrir el trabajo ${workId}:`, error); throw error; }
  }

  // --- CORRECCIÓN DE ERROR 4: Generar ID aquí ---
  async addItemToWorkshopWork(workId: string, item: Omit<WorkItem, 'id'>): Promise<void> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    const newItem: WorkItem = {
      ...item,
      id: doc(collection(this.firestore, '_')).id // Genera un ID único
    };
    try {
      const currentWorkSnap = await getDoc(workDocRef);
      if (!currentWorkSnap.exists()) { throw new Error('Trabajo no encontrado para agregar artículo.'); }
      const currentWork = currentWorkSnap.data() as WorkshopWork;
      if (currentWork.currentStatus === 'Ready' || currentWork.currentStatus === 'Completed') { throw new Error('Este trabajo está listo o completado y no se puede modificar.'); }
      const newTotalPrice = (currentWork.totalPrice || 0) + (newItem.unitPrice * newItem.quantity);
      await updateDoc(workDocRef, { addedItems: arrayUnion(newItem), totalPrice: newTotalPrice });
    } catch (error) { console.error('[Service] Error agregando artículo al trabajo:', error); throw error; }
  }

  async removeItemFromWorkshopWork(workId: string, itemToRemove: WorkItem): Promise<void> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    try {
      const currentWorkSnap = await getDoc(workDocRef);
      if (!currentWorkSnap.exists()) { throw new Error("Trabajo no encontrado para eliminar artículo."); }
      const currentWork = currentWorkSnap.data() as WorkshopWork;
      if (currentWork.currentStatus === 'Ready' || currentWork.currentStatus === 'Completed') { throw new Error('Este trabajo está listo o completado y no se puede modificar.'); }
      const itemCost = itemToRemove.unitPrice * itemToRemove.quantity;
      const newTotalPrice = (currentWork.totalPrice || 0) - itemCost;
      await updateDoc(workDocRef, { addedItems: arrayRemove(itemToRemove), totalPrice: newTotalPrice < 0 ? 0 : newTotalPrice });
    } catch (error) { console.error('[Service] Error eliminando artículo del trabajo:', error); throw error; }
  }

  // --- CORRECCIÓN DE ERROR 5: Firma actualizada ---
 async sendOrderReadyEmail(emailData: {
    to_email: string;
    client_name: string;
    order_number: string;
    pickup_instructions?: string;
    workshop_phone?: string;
  }): Promise<any> {
    try {
      const response = await this.http.post(`${this.EMAIL_API_URL}/send-order-email`, {
        to_email: emailData.to_email,
        client_name: emailData.client_name,
        order_number: emailData.order_number,
        pickup_instructions: emailData.pickup_instructions || 'Su bicicleta está lista para recoger. ',
        workshop_phone: emailData.workshop_phone || '(849)-846-8391'
      }).toPromise();

      console.log('Email sent successfully via backend:', response);
      return { success: true, response };
      
    } catch (error: any) {
      console.error('Error sending email via backend:', error);
      
      // Handle different error types
      if (error.status === 400) {
        return { success: false, error: 'Datos de email incompletos' };
      } else if (error.status === 500) {
        return { success: false, error: 'Error del servidor al enviar email' };
      } else if (error.status === 0) {
        return { success: false, error: 'No se puede conectar al servidor' };
      }
      
      return { success: false, error: 'Error inesperado al enviar email' };
    }
  }


  // --- MÉTODOS DE CLIENTES Y BICICLETAS (Sin recarga) ---
  getClients(): Observable<Client[]> {
    const clientsRef = collection(this.firestore, this.clientsCollection);
    const q = query(clientsRef, orderBy("name", "asc"));
    return collectionData(q, { idField: 'id' }) as Observable<Client[]>;
  }

  async getClientById(id: string): Promise<Client | null> {
    if (!id) return null;
    const clientDocRef = doc(this.firestore, this.clientsCollection, id);
    const docSnap = await getDoc(clientDocRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Client : null;
  }

  async createClient(clientData: Partial<Client> & { name: string }): Promise<string> {
    const clientsRef = collection(this.firestore, this.clientsCollection);
    const newClientData: any = { ...clientData, createdAt: serverTimestamp() };
    if (clientData.phone) newClientData.normalizedPhone = clientData.phone.replace(/\D/g, '');
    
    const docRef = await addDoc(clientsRef, newClientData);
    
    // Invalidamos el caché para forzar carga fresca
    sessionStorage.removeItem('clientsCache');
    this.loadClients();
    return docRef.id;
  }

  async updateClient(clientId: string, clientData: Partial<Client>): Promise<void> {
     const clientDocRef = doc(this.firestore, this.clientsCollection, clientId);
    const dataToUpdate: any = { ...clientData };
    if (clientData.phone) dataToUpdate.normalizedPhone = clientData.phone.replace(/\D/g, '');
    
    await updateDoc(clientDocRef, dataToUpdate);
    
    // Invalidamos el caché
    sessionStorage.removeItem('clientsCache');
    this.loadClients();
  }

  async deleteClient(clientId: string): Promise<void> {
    const clientDocRef = doc(this.firestore, this.clientsCollection, clientId);
    await deleteDoc(clientDocRef);
    
    // Invalidamos el caché
    sessionStorage.removeItem('clientsCache');
    this.loadClients();
  }

  getAppointmentsRange(start: Date, end: Date): Observable<Appointment[]> {
    const ref = collection(this.firestore, this.appointmentsCollection);
    const q = query(
      ref, 
      where('date', '>=', Timestamp.fromDate(start)),
      where('date', '<=', Timestamp.fromDate(end)),
      orderBy('date', 'asc')
    );
    return collectionData(q, { idField: 'id' }) as Observable<Appointment[]>;
  }

  async addAppointment(appointment: Omit<Appointment, 'id' | 'createdAt'>): Promise<string> {
    const ref = collection(this.firestore, this.appointmentsCollection);
    const docRef = await addDoc(ref, {
      ...appointment,
      createdAt: serverTimestamp()
    });
    return docRef.id;
  }

  async updateAppointmentStatus(id: string, status: AppointmentStatus): Promise<void> {
    const ref = doc(this.firestore, `${this.appointmentsCollection}/${id}`);
    await updateDoc(ref, { status });
  }

  async deleteAppointment(id: string): Promise<void> {
    const ref = doc(this.firestore, `${this.appointmentsCollection}/${id}`);
    await deleteDoc(ref);
  }

    getWorkByCaseNumber(caseNumber: string): Observable<WorkshopWork | null> {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("caseNumber", "==", caseNumber.toUpperCase().trim()));
    return from(getDocs(q)).pipe(
      map(snap => {
        if (snap.empty) return null;
        const doc = snap.docs[0];
        return { id: doc.id, ...doc.data() } as WorkshopWork;
      }),
      catchError(err => {
        console.error("Error buscando orden de seguimiento:", err);
        return of(null);
      })
    );
  }


  getBikesForClient(clientId: string): Observable<Bike[]> {
    const bikesRef = collection(this.firestore, this.bikesCollection);
    const q = query(bikesRef, where("clientId", "==", clientId));
    return collectionData(q, { idField: 'id' }) as Observable<Bike[]>;
  }

  async getBikeById(id: string): Promise<Bike | null> {
    if (!id) return null;
    const bikeDocRef = doc(this.firestore, this.bikesCollection, id);
    const docSnap = await getDoc(bikeDocRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as Bike : null;
  }

  async addBike(bikeData: Omit<Bike, 'id'>): Promise<string> {
    const bikesRef = collection(this.firestore, this.bikesCollection);
    const docRef = await addDoc(bikesRef, bikeData);
    return docRef.id;
  }

  async updateBike(bikeId: string, data: Partial<Bike>): Promise<void> {
    const bikeDocRef = doc(this.firestore, `${this.bikesCollection}/${bikeId}`);
    return updateDoc(bikeDocRef, data);
  }

  getWorksForClient(clientId: string): Observable<WorkshopWork[]> {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("clientId", "==", clientId));
    return (collectionData(q, { idField: 'id' }) as Observable<WorkshopWork[]>).pipe(
      map(works => works.sort((a, b) => {
        const timeA = a.entryDate?.toDate ? a.entryDate.toDate().getTime() : new Date(a.entryDate || 0).getTime();
        const timeB = b.entryDate?.toDate ? b.entryDate.toDate().getTime() : new Date(b.entryDate || 0).getTime();
        return timeB - timeA;
      })),
      catchError(err => {
        console.error("Error obteniendo trabajos del cliente:", err);
        return of([]);
      })
    );
  }

  getWorksForBike(bikeId: string): Observable<WorkshopWork[]> {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("bikeId", "==", bikeId));
    return (collectionData(q, { idField: 'id' }) as Observable<WorkshopWork[]>).pipe(
      map(works => works.sort((a, b) => {
        const timeA = a.entryDate?.toDate ? a.entryDate.toDate().getTime() : new Date(a.entryDate || 0).getTime();
        const timeB = b.entryDate?.toDate ? b.entryDate.toDate().getTime() : new Date(b.entryDate || 0).getTime();
        return timeB - timeA;
      })),
      catchError(err => {
        console.error("Error obteniendo trabajos de la bicicleta:", err);
        return of([]);
      })
    );
  }

  async getWorksForBikeOnce(bikeId: string): Promise<WorkshopWork[]> {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("bikeId", "==", bikeId));
    const snap = await getDocs(q);
    const works = snap.docs.map(d => ({ id: d.id, ...d.data() } as WorkshopWork));
    return works.sort((a, b) => {
      const timeA = a.entryDate?.toDate ? a.entryDate.toDate().getTime() : new Date(a.entryDate || 0).getTime();
      const timeB = b.entryDate?.toDate ? b.entryDate.toDate().getTime() : new Date(b.entryDate || 0).getTime();
      return timeB - timeA;
    });
  }
  
  updateBillingStatus(workId: string, newStatus: BillingStatus): Promise<void> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    return updateDoc(workDocRef, { billingStatus: newStatus });
  }
  
  async updateNotifiedStatus(workId: string, newStatus: NotifiedStatus): Promise<void> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    return updateDoc(workDocRef, { notifiedStatus: newStatus });
  }
  

  async validateAndAssignWorkItems(workId: string, updatedItems: WorkItem[]): Promise<void> {
    const workDocRef = doc(this.firestore, `${this.worksCollection}/${workId}`);
    return updateDoc(workDocRef, { 
      validatedByManager: true,
      addedItems: updatedItems // Se guardan los artículos con sus autores
    });
  }

   // --- NUEVA JUGADA MAESTRA: Fetch Optimizado por Mes ---
  getCompletedWorksByMonth(year: number, month: number): Observable<WorkshopWork[]> {
    // Calculamos el primer y último día del mes
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);
    
    const worksRef = collection(this.firestore, this.worksCollection);
    
    // NOTA PARA TI: Esta consulta combinada (currentStatus + Rango de Fechas) 
    // requerirá que crees un Índice Compuesto en la consola de Firebase.
    // El enlace para crearlo aparecerá en la consola del navegador si falla la primera vez.
    const q = query(
      worksRef, 
      where("currentStatus", "==", "Completed"),
      where("entryDate", ">=", Timestamp.fromDate(startOfMonth)),
      where("entryDate", "<=", Timestamp.fromDate(endOfMonth))
    );

    return from(getDocs(q)).pipe(
      map(querySnapshot => querySnapshot.docs.map(d => ({ id: d.id, ...d.data() } as WorkshopWork))),
      catchError(err => { 
        console.error("Error obteniendo trabajos del mes para comisiones:", err); 
        return of([]); 
      })
    );
  }

  // --- MÉTODOS DE RESUMEN (YA OPTIMIZADOS CON getDocs) ---
  getWorkCounts(): Observable<WorkCounts | null> {
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("currentStatus", "not-in", ["Cancelled"]));
    return from(getDocs(q)).pipe(
      map(querySnapshot => this.processWorkCounts(querySnapshot.docs.map(d => d.data() as WorkshopWork))),
      catchError(err => { console.error("Error obteniendo el conteo de trabajos:", err); return of(null); })
    );
  }

  getWeeklyWorkSummary(): Observable<WorkCounts | null> {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    const startTimestamp = Timestamp.fromDate(startOfWeek);
    const endTimestamp = Timestamp.fromDate(endOfWeek);
    const worksRef = collection(this.firestore, this.worksCollection);
    const q = query(worksRef, where("entryDate", ">=", startTimestamp), where("entryDate", "<=", endTimestamp));
    return from(getDocs(q)).pipe(
      map(querySnapshot => this.processWorkCounts(querySnapshot.docs.map(d => d.data() as WorkshopWork))),
      catchError(err => { console.error("Error obteniendo el resumen semanal:", err); return of(null); })
    );
  }

  private processWorkCounts(works: WorkshopWork[]): WorkCounts {
    const counts: WorkCounts = {
      'Received': 0, 'In Progress': 0, 'Awaiting Parts': 0, 'Ready': 0, 'Handed Off': 0, 'Completed': 0, 'Cancelled': 0,
    };
    works.forEach(work => {
      const status = work.currentStatus;
      if (status && counts.hasOwnProperty(status)) { counts[status]++; }
    });
    return counts;
  }
  
  private normalizePhoneNumber(phone: string): string {
    return phone.replace(/\D/g, ''); // Elimina todo lo que no sea un dígito
  }

  async checkIfClientExists(clientData: { email?: string; phone?: string }): Promise<{ exists: boolean, field: string | null }> {
    const clientsRef = collection(this.firestore, this.clientsCollection);
    const queries = [];
    if (clientData.email) { queries.push(where('email', '==', clientData.email)); }
    if (clientData.phone) {
      const normalizedPhone = this.normalizePhoneNumber(clientData.phone);
      if (normalizedPhone) { queries.push(where('normalizedPhone', '==', normalizedPhone)); }
    }
    if (queries.length === 0) { return { exists: false, field: null }; }
    const finalQuery = query(clientsRef, or(...queries));
    const querySnapshot = await getDocs(finalQuery);
    if (!querySnapshot.empty) {
      const docData = querySnapshot.docs[0].data();
      if (docData['email'] === clientData.email) { return { exists: true, field: 'email' }; }
      if (docData['normalizedPhone'] === this.normalizePhoneNumber(clientData.phone || '')) { return { exists: true, field: 'phone' }; }
    }
    return { exists: false, field: null };
  }


 private readonly employeesCollection = 'employees';
 private readonly commParamsCollection = 'commission_params';

  // --- MÉTODOS DE EMPLEADOS ---
  getEmployees(): Observable<Employee[]> {
    const ref = collection(this.firestore, 'employees');
    return collectionData(ref, { idField: 'id' }) as Observable<Employee[]>;
  }

  async addEmployee(employee: Omit<Employee, 'id'>): Promise<void> {
    const ref = collection(this.firestore, 'employees');
    await addDoc(ref, { ...employee, createdAt: serverTimestamp() });
  }

  async updateEmployee(id: string, data: Partial<Employee>): Promise<void> {
    const ref = doc(this.firestore, `employees/${id}`);
    await updateDoc(ref, data);
  }

  async deleteEmployee(id: string): Promise<void> {
    const ref = doc(this.firestore, `employees/${id}`);
    await deleteDoc(ref);
  }

  // --- MÉTODOS DE PARÁMETROS DE COMISIÓN ---
  getCommissionParams(): Observable<CommissionParams | null> {
    const ref = doc(this.firestore, 'commission_params/default');
    return docData(ref) as Observable<CommissionParams | null>;
  }

  async updateCommissionParams(params: Partial<CommissionParams>): Promise<void> {
    const ref = doc(this.firestore, 'commission_params/default');
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, params);
    } else {
      await updateDoc(ref, params);
    }
  }

getInspection(workId: string): Observable<VehicleInspection | null> {
    const docRef = doc(this.firestore, `bap_inspections/${workId}`);
    return docData(docRef, { idField: 'id' }) as Observable<VehicleInspection | null>;
  }

  async saveInspection(workId: string, data: VehicleInspection): Promise<void> {
    const docRef = doc(this.firestore, `bap_inspections/${workId}`);
    await setDoc(docRef, { 
      ...data, 
      updatedAt: serverTimestamp(), 
      createdAt: data.createdAt || serverTimestamp() 
    }, { merge: true });
  }

  async getInspectionsViewByMonth(year: number, month: number): Promise<{inspection: VehicleInspection, work: WorkshopWork}[]> {
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);
    
    // 1. Buscamos DIRECTAMENTE las inspecciones creadas en este mes
    // ¡Esto es 100 veces más rápido y ahorra lecturas en Firebase!
    const inspRef = collection(this.firestore, 'bap_inspections');
    const q = query(inspRef, 
      where('createdAt', '>=', Timestamp.fromDate(startOfMonth)), 
      where('createdAt', '<=', Timestamp.fromDate(endOfMonth))
    );
    
    const inspSnap = await getDocs(q);
    const results: {inspection: VehicleInspection, work: WorkshopWork}[] = [];
    
    // 2. Por cada inspección encontrada, traemos los datos de su orden de trabajo
    for (const iDoc of inspSnap.docs) {
      const inspData = iDoc.data() as VehicleInspection;
      inspData.id = iDoc.id; // Asignamos el ID a la inspección
      
      // Traemos el trabajo usando el mismo ID (ya que comparten el mismo ID de documento)
      const workDoc = await getDoc(doc(this.firestore, `workshop_works/${iDoc.id}`));
      
      if (workDoc.exists()) {
        const workData = workDoc.data() as WorkshopWork;
        workData.id = workDoc.id; // Inyectar el ID real de la orden
        
        results.push({ inspection: inspData, work: workData });
      }
    }
    
    // Ordenar de más reciente a más antigua por la fecha en que se hizo la inspección
    return results.sort((a, b) => {
      const dateA = a.inspection.createdAt?.toDate ? a.inspection.createdAt.toDate().getTime() : 0;
      const dateB = b.inspection.createdAt?.toDate ? b.inspection.createdAt.toDate().getTime() : 0;
      return dateB - dateA;
    });
  }

  async searchInspectionsFullDB(searchTerm: string): Promise<{inspection: VehicleInspection, work: WorkshopWork}[]> {
    const worksRef = collection(this.firestore, 'workshop_works');
    const termClean = searchTerm.trim();
    const termUpper = termClean.toUpperCase();
    
    // 1. Intentamos búsqueda exacta por Caso (Ej. TRB-15)
    const qCase = query(worksRef, where('caseNumber', '==', termUpper));
    const caseSnap = await getDocs(qCase);
    
    let workDocs = caseSnap.docs;

    // 2. Si no hay por caso, intentamos por Cliente
    if (workDocs.length === 0) {
      const termCapitalized = termClean.charAt(0).toUpperCase() + termClean.slice(1).toLowerCase();
      const qClient = query(worksRef, 
        where('clientName', '>=', termCapitalized), 
        where('clientName', '<=', termCapitalized + '\uf8ff')
      );
      const clientSnap = await getDocs(qClient);
      workDocs = clientSnap.docs;
    }

    const results: {inspection: VehicleInspection, work: WorkshopWork}[] = [];
    
    // Filtramos cuáles de esos trabajos encontrados realmente tienen una inspección
    for (const wDoc of workDocs) {
      const workData = wDoc.data() as WorkshopWork;
      workData.id = wDoc.id; 
      
      const inspDoc = await getDoc(doc(this.firestore, `bap_inspections/${wDoc.id}`));
      
      if (inspDoc.exists()) {
        if (
          workData.caseNumber.includes(termUpper) || 
          workData.clientName.toLowerCase().includes(termClean.toLowerCase()) ||
          workData.bikeDescription.toLowerCase().includes(termClean.toLowerCase())
        ) {
          const inspData = inspDoc.data() as VehicleInspection;
          inspData.id = inspDoc.id; 
          
          results.push({ inspection: inspData, work: workData });
        }
      }
    }
    return results;
  }

  async deleteInspection(workId: string): Promise<void> {
    const docRef = doc(this.firestore, `bap_inspections/${workId}`);
    await deleteDoc(docRef);
  } 


}